import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import type { HomeMode } from '../../lib/homeMode.js';
import { hapticTap } from '../../lib/native.js';
import { withTransition } from '../../lib/transition.js';
import './home-mode.css';

/**
 * Seletor de contexto no alto da Início (T7.29, no lugar das chips Pessoal · Painel ·
 * Clínica): mostra onde a pessoa está e abre a lista dos lugares dela (o Pessoal e cada
 * negócio), com a troca sem sair da conta. Cabe mais de um negócio sem apertar a barra.
 */
export function HomeModeSwitch({
  mode,
  options,
  clinicName,
  onPick,
}: {
  mode: HomeMode;
  options: HomeMode[];
  clinicName: string | null;
  onPick: (mode: HomeMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const esc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', away, true);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointerdown', away, true);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);

  const places: { key: HomeMode; label: string; sub: string; mark: string }[] = options.map(
    (key) =>
      key === 'clinica'
        ? {
            key,
            label: clinicName ?? 'Clínica',
            sub: 'Agenda, pacientes e financeiro',
            mark: (clinicName ?? 'C').trim().charAt(0).toUpperCase(),
          }
        : { key, label: 'Pessoal', sub: 'Seu dia, dinheiro, saúde e pessoas', mark: '' },
  );
  const current = places.find((place) => place.key === mode) ?? places[0];

  return (
    <div className="hm-ctx" ref={box}>
      <button
        type="button"
        className={`hm-ctx-btn${open ? ' open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Você está em ${current?.label ?? 'Pessoal'}. Trocar`}
        onClick={() => {
          hapticTap();
          setOpen((value) => !value);
        }}
      >
        <span>{current?.label ?? 'Pessoal'}</span>
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M7 10l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="hm-ctx-menu" role="menu" aria-label="Onde você quer estar">
          <p className="hm-ctx-hd">Onde você quer estar</p>
          {places.map((place) => (
            <button
              key={place.key}
              type="button"
              role="menuitemradio"
              aria-checked={place.key === mode}
              className={`hm-ctx-it${place.key === mode ? ' on' : ''}`}
              onClick={() => {
                hapticTap();
                setOpen(false);
                // Troca de contexto: a Início esmaece e entra a outra (300 ms).
                if (place.key !== mode) withTransition('ctx', () => onPick(place.key));
              }}
            >
              <span
                className={`hm-ctx-mark${place.key === 'pessoal' ? ' me' : ''}`}
                aria-hidden="true"
              >
                {place.key === 'pessoal' ? (
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <circle
                      cx="12"
                      cy="8.6"
                      r="3.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M5.5 19c.8-3.4 3.3-5.2 6.5-5.2s5.7 1.8 6.5 5.2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  place.mark
                )}
              </span>
              <span className="hm-ctx-t">
                <b>{place.label}</b>
                <small>{place.sub}</small>
              </span>
              {place.key === mode && (
                <svg
                  className="hm-ctx-ok"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    d="M5.5 12.5l4 4 9-9.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className="hm-ctx-it add"
            onClick={() => {
              hapticTap();
              setOpen(false);
              navigate('/negocio');
            }}
          >
            <span className="hm-ctx-mark add" aria-hidden="true">
              +
            </span>
            <span className="hm-ctx-t">
              <b>Novo negócio</b>
              <small>Clínica, loja ou serviço</small>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
